// ============================================================
// FieldCursorOverlay.tsx  (Stage A — live form-field cursors, PILOT)
//
// Renders a REMOTE user's live text caret + avatar flag at their exact caret
// pixel inside a native <input>. Native inputs can't hold children, so we MEASURE
// the caret's x-offset with an off-screen MIRROR element that copies the input's
// exact text metrics (font, letter-spacing, padding, …), then place an
// absolutely-positioned 2px cursor line + a PresenceAvatar flag (matching the
// Yjs rich-text cursor look) over the input.
//
// Purely presentational + pointer-events:none — it can never block typing, and a
// wrong pixel is cosmetic, never a data bug. FormField mounts it only when a
// remote owner is present on an eligible (non-address-search) field; it then
// renders only for an eligible input TYPE with a live remote caret (so a blur,
// an ineligible input, or no caret all hide it).
// ============================================================
import React, { useCallback, useEffect, useRef, useState } from 'react'
import PresenceAvatar from './PresenceAvatar'
import { useRemoteFieldCaret, CARET_ELIGIBLE_INPUT_TYPES } from '../../hooks/usePresence'
import { DEFAULT_AVATAR_COLOUR } from './Avatar'

interface FieldCursorOverlayProps {
  // The FormField's `relative` wrapper — the overlay's positioning context; we
  // query the <input> inside it and measure offsets against its rect.
  wrapperRef: React.RefObject<HTMLDivElement | null>
  fieldKey: string
  // Whether a field-owner is present on this field (from FormField). Gates
  // visibility so a blur (owner removed) hides the cursor even if a stale caret
  // message lingers.
  active: boolean
}

// Computed text metrics to copy onto the mirror so its measurement matches the
// input's rendering exactly.
const METRIC_PROPS = [
  'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'fontVariant',
  'letterSpacing', 'textTransform', 'textIndent', 'wordSpacing',
] as const

// Measure the pixel width of `text` as the input would render it, via a hidden
// mirror that copies the input's metrics. `whiteSpace:pre` preserves spaces; a
// zero-width trailing marker gives the text's right edge via offsetLeft. We
// measure the SENDER's caret-prefix text (from the message), NOT the receiver's
// local value — so the cursor is correct even while the value is mid-sync.
const measureTextWidth = (style: CSSStyleDeclaration, text: string): number => {
  const mirror = document.createElement('div')
  for (const p of METRIC_PROPS) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(mirror.style as any)[p] = (style as any)[p]
  }
  mirror.style.position = 'absolute'
  mirror.style.top = '0'
  mirror.style.left = '-9999px'
  mirror.style.visibility = 'hidden'
  mirror.style.whiteSpace = 'pre'
  mirror.textContent = text
  const marker = document.createElement('span')
  marker.textContent = '​'
  mirror.appendChild(marker)
  document.body.appendChild(mirror)
  const width = marker.offsetLeft
  document.body.removeChild(mirror)
  return width
}

interface CaretPos { left: number; top: number; height: number; visible: boolean }

const FieldCursorOverlay: React.FC<FieldCursorOverlayProps> = ({ wrapperRef, fieldKey, active }) => {
  const caret = useRemoteFieldCaret(fieldKey)
  const [pos, setPos] = useState<CaretPos | null>(null)

  // Latest inputs to the measurement, in refs so the event listeners can always
  // read the freshest values without re-binding.
  const caretRef = useRef(caret)
  const activeRef = useRef(active)
  caretRef.current = caret
  activeRef.current = active

  const recompute = useCallback((): void => {
    const wrap = wrapperRef.current
    const input = wrap?.querySelector('input') as HTMLInputElement | null
    const c = caretRef.current
    if (!wrap || !input || !c || !activeRef.current) { setPos(null); return }
    // Only draw a mirror cursor for eligible single-line text inputs. This
    // excludes checkbox/radio/date/color/etc. (a <select> or Yjs contenteditable
    // has no matching <input>, so those already fall through above / here).
    if (!CARET_ELIGIBLE_INPUT_TYPES.has(input.type)) { setPos(null); return }

    const style = getComputedStyle(input)
    // Measure against the SENDER's full value (from the message), sliced to the
    // caret — so the cursor sits at the sender's real caret even while the value
    // is mid-sync locally. Fall back to the local value if a message lacks it.
    const fullValue = typeof c.caretText === 'string' ? c.caretText : input.value
    const prefix = fullValue.slice(0, Math.max(0, Math.min(c.selStart, fullValue.length)))
    const textWidth = measureTextWidth(style, prefix)
    const paddingLeft = parseFloat(style.paddingLeft) || 0
    const borderLeft = parseFloat(style.borderLeftWidth) || 0
    const paddingRight = parseFloat(style.paddingRight) || 0
    const borderRight = parseFloat(style.borderRightWidth) || 0

    // Caret x within the input's box, accounting for horizontal scroll.
    const caretX = paddingLeft + borderLeft + textWidth - input.scrollLeft

    const inRect = input.getBoundingClientRect()
    const wrapRect = wrap.getBoundingClientRect()

    // Clip: hide when the caret is scrolled out of the visible content band.
    const minX = paddingLeft + borderLeft
    const maxX = inRect.width - paddingRight - borderRight
    const visible = caretX >= minX - 0.5 && caretX <= maxX + 0.5

    const fontSize = parseFloat(style.fontSize) || 14
    const height = fontSize * 1.25
    const left = (inRect.left - wrapRect.left) + caretX
    const top = (inRect.top - wrapRect.top) + inRect.height / 2
    setPos({ left, top, height, visible })
  }, [wrapperRef])

  // rAF-batched recompute so bursts (scroll + input in the same frame) coalesce.
  const rafRef = useRef<number | null>(null)
  const schedule = useCallback((): void => {
    if (rafRef.current !== null) return
    rafRef.current = window.requestAnimationFrame(() => { rafRef.current = null; recompute() })
  }, [recompute])

  // Recompute on: remote caret change, active change, input scroll + value
  // change, window resize, input resize (ResizeObserver), and font load.
  useEffect(() => {
    schedule()
    const wrap = wrapperRef.current
    const input = wrap?.querySelector('input') as HTMLInputElement | null
    if (!input) return
    input.addEventListener('scroll', schedule)
    input.addEventListener('input', schedule)
    window.addEventListener('resize', schedule)
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null
    ro?.observe(input)
    // Fonts can load after first paint → the measurement shifts; recompute once.
    const fonts = (document as unknown as { fonts?: { ready?: Promise<unknown> } }).fonts
    let cancelled = false
    fonts?.ready?.then(() => { if (!cancelled) schedule() }).catch(() => {})
    return () => {
      cancelled = true
      input.removeEventListener('scroll', schedule)
      input.removeEventListener('input', schedule)
      window.removeEventListener('resize', schedule)
      ro?.disconnect()
      if (rafRef.current !== null) { window.cancelAnimationFrame(rafRef.current); rafRef.current = null }
    }
  }, [schedule, wrapperRef, caret, active])

  if (!caret || !active || !pos || !pos.visible) return null
  const colour = caret.avatarColour && caret.avatarColour.length > 0 ? caret.avatarColour : DEFAULT_AVATAR_COLOUR
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute z-10"
      style={{ left: pos.left, top: pos.top - pos.height / 2, height: pos.height }}
    >
      {/* the 2px caret line, owner colour */}
      <div style={{ width: 2, height: '100%', backgroundColor: colour, borderRadius: 1 }} />
      {/* the avatar flag floating above the caret — same chip as the Yjs cursor */}
      <span className="absolute" style={{ top: -22, left: -1 }}>
        <PresenceAvatar userId={caret.userId} name={caret.name} colour={caret.avatarColour} photoUrl={caret.avatarPhotoUrl} />
      </span>
    </div>
  )
}

export default FieldCursorOverlay
